// src/authpages/timers/StudyTimers.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Play, Pause, RotateCcw, Clock } from 'lucide-react';
import { 
  auth, 
  onAuthStateChanged
} from '../../firebase';

// Import timer utility functions
import { 
  getTodayProgress, 
  storeLocalProgress, 
  recordStudySession, 
  checkForDayChange 
} from './timerUtils';

// Move timer settings outside component to prevent re-renders
const TIMER_SETTINGS = {
  pomodoro: {
    workTime: 25,
    breakTime: 5,
    longBreakTime: 15,
    longBreakInterval: 4
  },
  flow: {
    workTime: 52,
    breakTime: 17
  },
  energy: {
    workTime: 90,
    breakTime: 20
  },
  peak: {
    workTime: 58,
    breakTime: 17
  },
  custom: {
    workTime: 30,
    breakTime: 10
  }
};

// Simple UI components to replace the shadcn/ui ones
const Card = ({ className, children }) => (
  <div className={`rounded-lg border border-gray-200 shadow-sm ${className || ''}`}>
    {children}
  </div>
);

const CardHeader = ({ children }) => (
  <div className="px-6 py-4 border-b border-gray-200">
    {children}
  </div>
);

const CardTitle = ({ children }) => (
  <h3 className="text-lg font-semibold">
    {children}
  </h3>
);

const CardContent = ({ className, children }) => (
  <div className={`p-6 ${className || ''}`}>
    {children}
  </div>
);

const Button = ({ onClick, className, variant, size, style, children }) => {
  let buttonClass = "flex items-center justify-center rounded-md font-medium";
  
  // Handle size
  if (size === "lg") {
    buttonClass += " px-5 py-2.5 text-base";
  } else {
    buttonClass += " px-4 py-2 text-sm";
  }
  
  // Handle variant
  if (variant === "outline") {
    buttonClass += " border border-current";
  } else {
    buttonClass += " text-white";
  }
  
  return (
    <button 
      onClick={onClick} 
      className={`${buttonClass} ${className || ''}`}
      style={style}
    >
      {children}
    </button>
  );
};

const StudyTimers = () => {
  const [timeLeft, setTimeLeft] = useState(TIMER_SETTINGS.pomodoro.workTime * 60);
  const [isRunning, setIsRunning] = useState(false);
  const [mode, setMode] = useState('work');
  const [pomodoroCount, setPomodoroCount] = useState(0);
  const [customWorkTime, setCustomWorkTime] = useState(30);
  const [customBreakTime, setCustomBreakTime] = useState(10);
  const [totalSessions, setTotalSessions] = useState(0);
  const [totalMinutes, setTotalMinutes] = useState(0);
  const [totalCycles, setTotalCycles] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pomodoro');
  const [user, setUser] = useState(null);
  
  // Use a ref to store the interval ID for cleanup
  const timerIntervalRef = useRef(null);
  
  // DEBUG: Add logging to track component lifecycle
  console.log("StudyTimers component rendered");

  // Add at the beginning of the StudyTimers component
  useEffect(() => {
    const checkAuth = () => {
      const user = auth.currentUser;
      console.log("Auth check:", !!user);
      if (user) {
        console.log("User ID:", user.uid);
        console.log("User email:", user.email);
      } else {
        console.log("No user logged in");
      }
    };
    
    checkAuth();
    // Check again after a short delay in case auth is still initializing
    setTimeout(checkAuth, 1000);
  }, []);

  // Check for authentication
  useEffect(() => {
    console.log("Setting up auth listener");
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        console.log("User authenticated:", currentUser.email, currentUser.uid);
      } else {
        console.log("No user authenticated");
      }
    });
    
    return () => unsubscribe();
  }, []);

  // Load saved progress on component mount
  useEffect(() => {
    const loadProgress = () => {
      try {
        setIsLoading(true);
        
        // Check for day change and reset if needed
        checkForDayChange();
        
        // Get today's progress
        const todayProgress = getTodayProgress();
        console.log("Loaded today's progress:", todayProgress);
        
        setTotalSessions(todayProgress.sessions || 0);
        setTotalMinutes(todayProgress.minutes || 0);
        setTotalCycles(todayProgress.cycles || 0);
        setPomodoroCount(todayProgress.sessions || 0);
        
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading progress:', error);
        setIsLoading(false);
      }
    };
    
    loadProgress();
    
    // Check for day change every minute
    const refreshInterval = setInterval(() => {
      loadProgress();
    }, 60000);
    
    return () => clearInterval(refreshInterval);
  }, []);

  // FIX: Add a useEffect to handle the timer countdown when isRunning is true
  useEffect(() => {
    if (isRunning) {
      // Clear any existing interval to avoid duplicates
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
      
      timerIntervalRef.current = setInterval(() => {
        setTimeLeft(prevTime => {
          if (prevTime <= 1) {
            // Timer has reached zero - clear interval and handle completion
            clearInterval(timerIntervalRef.current);
            handleTimerComplete();
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);
    } else if (timerIntervalRef.current) {
      // If timer is paused, clear the interval
      clearInterval(timerIntervalRef.current);
    }
    
    // Cleanup function to clear interval when component unmounts or isRunning changes
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [isRunning]); // Only re-run when isRunning changes

  // FIX: Update timer display when activeTab changes
  useEffect(() => {
    // Only update the timer if it's not running
    if (!isRunning) {
      resetTimer();
    }
  }, [activeTab]);
  
  // FIX: Update timer settings when custom values change
  useEffect(() => {
    if (activeTab === 'custom' && !isRunning) {
      // Update the custom timer settings
      TIMER_SETTINGS.custom = {
        workTime: customWorkTime,
        breakTime: customBreakTime
      };
      
      // If in work mode, update timeLeft with new custom work time
      if (mode === 'work') {
        setTimeLeft(customWorkTime * 60);
      } else {
        setTimeLeft(customBreakTime * 60);
      }
    }
  }, [customWorkTime, customBreakTime, activeTab, isRunning, mode]);

  const handleTimerComplete = useCallback(() => {
    console.log("Timer complete! Mode:", mode);
    
    if (mode === 'work') {
      // Use activeTab state to determine which timer technique was used
      const timerTechnique = activeTab || 'pomodoro';
      
      // Calculate the actual work session duration in minutes
      const sessionMinutes = TIMER_SETTINGS[timerTechnique].workTime;
      
      console.log(`Completing ${sessionMinutes} minute ${timerTechnique} work session`);
      
      // Store progress in local storage and update UI
      const newProgress = storeLocalProgress(timerTechnique, sessionMinutes);
      if (newProgress) {
        setPomodoroCount(newProgress.sessions);
        setTotalSessions(newProgress.sessions);
        setTotalMinutes(newProgress.minutes);
        setTotalCycles(newProgress.cycles);
      }
      
      // Record session to Firebase with additional options for rewards
      try {
        // Add options object with details for rewards tracking
        recordStudySession(timerTechnique, sessionMinutes, {
          hadBreaks: false,
          quizCompleted: false, 
          quizDurationSeconds: 0,
          studyPartners: [],
          topicId: '',
          notes: `Completed ${sessionMinutes} minute ${timerTechnique} study session`
        });
        
        // Set a flag in localStorage to trigger dashboard refresh
        localStorage.setItem('studySessionCompleted', 'true');
        localStorage.setItem('studySessionCompletedAt', new Date().toISOString());
      } catch (error) {
        console.error('Failed to record study session:', error);
      }
      
      // Set up the break
      if (timerTechnique === 'pomodoro' && 
          (pomodoroCount + 1) % TIMER_SETTINGS.pomodoro.longBreakInterval === 0) {
        setMode('longBreak');
        setTimeLeft(TIMER_SETTINGS.pomodoro.longBreakTime * 60);
      } else {
        setMode('break');
        setTimeLeft(TIMER_SETTINGS[timerTechnique].breakTime * 60);
      }
    } else {
      // If we're completing a break, transition back to work
      setMode('work');
      setTimeLeft(TIMER_SETTINGS[activeTab || 'pomodoro'].workTime * 60);
    }
  }, [mode, pomodoroCount, activeTab, user]);

  const toggleTimer = async () => {
    // When pausing a work timer that has time left, consider it a partial session
    if (isRunning && mode === 'work' && timeLeft > 0) {
      // Use activeTab state
      const timerTechnique = activeTab || 'pomodoro';
      
      // Calculate how many minutes were actually used
      const totalTimeForSession = TIMER_SETTINGS[timerTechnique].workTime * 60;
      const timeUsed = totalTimeForSession - timeLeft;
      const minutesUsed = Math.ceil(timeUsed / 60); // Round up to give credit for partial minutes
      
      if (minutesUsed > 0) {
        console.log(`Recording partial session: ${minutesUsed} minutes`);
        
        // Only count it if at least a minute was used
        const newProgress = storeLocalProgress(timerTechnique, minutesUsed);
        if (newProgress) {
          setPomodoroCount(newProgress.sessions);
          setTotalSessions(newProgress.sessions);
          setTotalMinutes(newProgress.minutes);
          setTotalCycles(newProgress.cycles);
        }
        
        // Record partial session to Firebase with reward options
        try {
          // Add options object with details for rewards tracking
          await recordStudySession(timerTechnique, minutesUsed, {
            hadBreaks: true, // Partial sessions always count as having breaks
            quizCompleted: false,
            quizDurationSeconds: 0,
            studyPartners: [],
            topicId: '',
            notes: `Partial ${minutesUsed} minute ${timerTechnique} study session`
          });
        } catch (error) {
          console.error('Failed to record partial study session:', error);
        }
      }
    }
    
    // Toggle timer state
    setIsRunning(!isRunning);
  };
  
  const resetTimer = () => {
    setIsRunning(false);
    setMode('work');
    setTimeLeft(TIMER_SETTINGS[activeTab || 'pomodoro'].workTime * 60);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const startPresetTimer = (technique, type) => {
    setIsRunning(false);
    setMode(type);
    setTimeLeft(TIMER_SETTINGS[technique][`${type}Time`] * 60);
    setIsRunning(true);
  };

  const handleCustomWorkTimeChange = (e) => {
    const value = parseInt(e.target.value) || 1;
    setCustomWorkTime(Math.min(Math.max(value, 1), 120)); // Limit between 1-120 minutes
  };

  const handleCustomBreakTimeChange = (e) => {
    const value = parseInt(e.target.value) || 1;
    setCustomBreakTime(Math.min(Math.max(value, 1), 60)); // Limit between 1-60 minutes
  };

  const handleTabChange = (tabValue) => {
    setActiveTab(tabValue);
    
    // FIX: Reset the timer when changing tabs
    setIsRunning(false);
    setMode('work');
    
    // If custom tab, use the custom settings
    if (tabValue === 'custom') {
      setTimeLeft(customWorkTime * 60);
    } else {
      setTimeLeft(TIMER_SETTINGS[tabValue].workTime * 60);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-semibold">Study Timer</h1>
        <p className="text-gray-600">Choose your study method and track your focus time</p>
      </div>

      <div className="grid gap-6">
        {/* Timer Display Card */}
        <Card className="bg-white">
          <CardContent className="pt-6">
            <div className="text-center mb-6">
              <div className="text-7xl font-bold mb-2 text-blue-600">{formatTime(timeLeft)}</div>
              <div className="text-lg text-gray-600">
                {mode === 'work' ? 'Focus Time' : mode === 'break' ? 'Short Break' : 'Long Break'}
              </div>
            </div>
            
            <div className="flex justify-center gap-4">
              <Button 
                style={{ 
                  backgroundColor: isRunning ? '#f97316' : '#22c55e',
                  color: 'white'
                }}
                className="w-32"
                size="lg"
                onClick={toggleTimer}
              >
                {isRunning ? <Pause className="mr-2 h-5 w-5" /> : <Play className="mr-2 h-5 w-5" />}
                {isRunning ? 'Pause' : 'Start'}
              </Button>
              <Button 
                variant="outline" 
                className="w-32 border-blue-600 text-blue-600 hover:bg-blue-50"
                size="lg"
                onClick={resetTimer}
              >
                <RotateCcw className="mr-2 h-5 w-5" />
                Reset
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Study Methods Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-blue-600" />
              <CardTitle>Study Methods</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="w-full">
              {/* Tabs navigation */}
              <div className="flex w-full mb-4 bg-blue-50 rounded-md">
                {["pomodoro", "flow", "energy", "peak", "custom"].map(tab => (
                  <button
                    key={tab}
                    onClick={() => handleTabChange(tab)}
                    className={`flex-1 px-4 py-2 text-center font-medium rounded-md 
                      ${activeTab === tab 
                        ? 'bg-blue-600 text-white' 
                        : 'text-gray-700 hover:bg-blue-100'}`}
                    data-state={activeTab === tab ? "active" : "inactive"}
                    value={tab}
                  >
                    {tab === "pomodoro" ? "Pomodoro" : 
                     tab === "flow" ? "Flow Master" : 
                     tab === "energy" ? "Energy Cycle" : 
                     tab === "peak" ? "Peak Focus" : "Custom"}
                  </button>
                ))}
              </div>

              {/* Tab content */}
              <div>
                {/* Pomodoro content */}
                {activeTab === "pomodoro" && (
                  <div className="space-y-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h3 className="font-medium mb-2">Pomodoro Technique</h3>
                      <p className="text-gray-600 text-sm">25 minutes of focused work, followed by 5-minute breaks. After 4 sessions, take a longer 15-minute break.</p>
                    </div>
                    <div className="flex gap-4">
                      <Button 
                        onClick={() => startPresetTimer('pomodoro', 'work')} 
                        className="flex-1"
                        style={{ backgroundColor: '#22c55e', color: 'white' }}
                      >
                        Start 25min Focus
                      </Button>
                      <Button 
                        onClick={() => startPresetTimer('pomodoro', 'break')} 
                        className="flex-1"
                        style={{ backgroundColor: '#f97316', color: 'white' }}
                      >
                        Start 5min Break
                      </Button>
                    </div>
                  </div>
                )}

                {/* Flow content */}
                {activeTab === "flow" && (
                  <div className="space-y-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h3 className="font-medium mb-2">Flow Master Method</h3>
                      <p className="text-gray-600 text-sm">52 minutes of deep work followed by 17 minutes of rest. Optimized for achieving flow state.</p>
                    </div>
                    <div className="flex gap-4">
                      <Button 
                        onClick={() => startPresetTimer('flow', 'work')} 
                        className="flex-1"
                        style={{ backgroundColor: '#22c55e', color: 'white' }}
                      >
                        Start 52min Flow
                      </Button>
                      <Button 
                        onClick={() => startPresetTimer('flow', 'break')} 
                        className="flex-1"
                        style={{ backgroundColor: '#f97316', color: 'white' }}
                      >
                        Start 17min Break
                      </Button>
                    </div>
                  </div>
                )}

                {/* Energy content */}
                {activeTab === "energy" && (
                  <div className="space-y-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h3 className="font-medium mb-2">Energy Cycle Method</h3>
                      <p className="text-gray-600 text-sm">90 minutes of focused work aligned with your natural energy cycles, followed by 20 minutes of recovery.</p>
                    </div>
                    <div className="flex gap-4">
                      <Button 
                        onClick={() => startPresetTimer('energy', 'work')} 
                        className="flex-1"
                        style={{ backgroundColor: '#22c55e', color: 'white' }}
                      >
                        Start 90min Cycle
                      </Button>
                      <Button 
                        onClick={() => startPresetTimer('energy', 'break')} 
                        className="flex-1"
                        style={{ backgroundColor: '#f97316', color: 'white' }}
                      >
                        Start 20min Break
                      </Button>
                    </div>
                  </div>
                )}

                {/* Peak content */}
                {activeTab === "peak" && (
                  <div className="space-y-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h3 className="font-medium mb-2">Peak Focus Method</h3>
                      <p className="text-gray-600 text-sm">58 minutes of intensive focus followed by 17 minutes of rest. Based on productivity research.</p>
                    </div>
                    <div className="flex gap-4">
                      <Button 
                        onClick={() => startPresetTimer('peak', 'work')} 
                        className="flex-1"
                        style={{ backgroundColor: '#22c55e', color: 'white' }}
                      >
                        Start 58min Focus
                      </Button>
                      <Button 
                        onClick={() => startPresetTimer('peak', 'break')} 
                        className="flex-1"
                        style={{ backgroundColor: '#f97316', color: 'white' }}
                      >
                        Start 17min Break
                      </Button>
                    </div>
                  </div>
                )}

                {/* Custom content */}
                {activeTab === "custom" && (
                  <div className="space-y-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h3 className="font-medium mb-2">Custom Timer</h3>
                      <p className="text-gray-600 text-sm">Create your own focus and break intervals that work best for your study style.</p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-6 mb-4">
                      <div>
                        <div className="block mb-2 text-sm font-medium">
                          Focus Time (minutes)
                        </div>
                        <div className="flex items-center h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2">
                          <input
                            type="number"
                            min="1"
                            max="120"
                            value={customWorkTime}
                            onChange={handleCustomWorkTimeChange}
                            className="w-full bg-transparent outline-none"
                          />
                        </div>
                      </div>
                      <div>
                        <div className="block mb-2 text-sm font-medium">
                          Break Time (minutes)
                        </div>
                        <div className="flex items-center h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2">
                          <input
                            type="number"
                            min="1"
                            max="60"
                            value={customBreakTime}
                            onChange={handleCustomBreakTimeChange}
                            className="w-full bg-transparent outline-none"
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex gap-4">
                      <Button 
                        onClick={() => startPresetTimer('custom', 'work')} 
                        className="flex-1"
                        style={{ backgroundColor: '#22c55e', color: 'white' }}
                      >
                        Start {customWorkTime}min Focus
                      </Button>
                      <Button 
                        onClick={() => startPresetTimer('custom', 'break')} 
                        className="flex-1"
                        style={{ backgroundColor: '#f97316', color: 'white' }}
                      >
                        Start {customBreakTime}min Break
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Today's Progress Card */}
        <Card className="bg-white shadow-sm">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-blue-600" />
              <CardTitle>Today's Progress</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg text-center">
                <p className="text-sm text-gray-600 mb-1">Sessions</p>
                <p className="text-xl font-bold text-blue-600">{totalSessions}</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg text-center">
                <p className="text-sm text-gray-600 mb-1">Minutes</p>
                <p className="text-xl font-bold text-green-600">{totalMinutes}</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg text-center">
                <p className="text-sm text-gray-600 mb-1">Cycles</p>
                <p className="text-xl font-bold text-purple-600">{totalCycles}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Authentication status indicator (for debugging) */}
        <div className="text-xs text-gray-500 text-center">
          {user ? `Logged in as: ${user.email} (${user.uid})` : 'Not logged in - sessions will only be stored locally'}
        </div>
      </div>
    </div>
  );
};

export default StudyTimers;